import {
  Autocomplete,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import React, { useCallback, useMemo, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

import { VirtualizedListboxComponent } from 'components/common/VirtualizedListboxComponent'
import Icon from 'components/Icon'
import {
  FilterOptionKey,
  FILTER_OPTIONS,
  getScenarioFilterBy,
  ScenarioTypeFilterBy,
  type TestSuiteScenarioOption
} from 'components/test-suites/forms/test-suite-composition/test-suite-composition-utils'
import { TestSuiteCompositionInputFieldHelpText } from 'components/test-suites/forms/test-suite-composition/TestSuiteCompositionInputFieldHelpText'
import { TestSuiteScenarioSearchOption } from 'components/test-suites/forms/test-suite-composition/TestSuiteScenarioSearchOption'
import { TestSuiteTestDetailsDrawer } from 'components/test-suites/forms/test-suite-composition/TestSuiteTestDetailsDrawer'
import {
  getOwningTeamLabel,
  hasValidScenarioTargets,
  type TestSuiteFormValues,
  type TestSuiteScenario
} from 'components/test-suites/forms/test-suite-form-utils'
import { useTestSuiteFormContext } from 'components/test-suites/forms/TestSuiteFormContext'
import useAuth from 'hooks/use-auth'
import theme from 'theme'
import { type Team } from 'types/api/teams'
import { createLookupMapFromArray } from 'utils/obj'

export const TestSuiteScenarioSelection: React.FC<{
  teams: Team[]
}> = ({ teams }) => {
  const { scenariosAggregated: scenarios, scenariosLookup } =
    useTestSuiteFormContext()

  const { control } = useFormContext<TestSuiteFormValues>()

  const { currentTeamId } = useAuth()
  const [selectedScenarioOption, setSelectedScenarioOption] =
    useState<TestSuiteScenarioOption | null>(null)
  const [selectedFilterKey, setSelectedFilterKey] =
    useState<FilterOptionKey>('all')
  const { setValue } = useFormContext<TestSuiteFormValues>()
  const [existingScenarios] = useWatch({
    control,
    name: ['scenarios']
  })

  const teamIdMap = useMemo(
    () => createLookupMapFromArray(teams, 'identifier'),
    [teams]
  )

  const handleSelectScenario = useCallback((_e, newValue) => {
    setSelectedScenarioOption(newValue)
  }, [])

  const handleAddScenario = useCallback(
    (newScenario: TestSuiteScenario) => {
      const duplicateScenario = existingScenarios.find(
        (existingScenario) =>
          existingScenario.scenarioId === newScenario.scenarioId
      )

      if (!duplicateScenario) {
        setValue('scenarios', [...existingScenarios, newScenario], {
          shouldValidate: true,
          shouldDirty: true
        })
      }

      setSelectedScenarioOption(null)
    },
    [existingScenarios, setValue]
  )

  const scenarioOptions: TestSuiteScenarioOption[] = useMemo(() => {
    const existingScenariosIdLookup = new Set(
      existingScenarios.map(({ scenarioId }) => scenarioId)
    )

    return scenarios
      .filter((scenario) => !existingScenariosIdLookup.has(scenario.guid))
      .map((scenario) => {
        const teamId = 'org_id' in scenario ? scenario.org_id : undefined
        const owningTeam = teamId ? teamIdMap[teamId] : undefined
        const owningTeamName = owningTeam && owningTeam.name
        const isCurrentTeam = teamId === currentTeamId

        return {
          section: owningTeamName ?? 'Recommended & Shared',
          filterBy: getScenarioFilterBy(isCurrentTeam, owningTeam),
          guid: scenario.guid,
          label: scenario.name,
          orgId: teamId,
          owningTeamLabel: getOwningTeamLabel(teamId, owningTeam)
        }
      })
      .sort((a, b) => {
        // order: current team, recommended & shared, others
        const isCurrentTeamA = a.orgId === currentTeamId
        const isCurrentTeamB = b.orgId === currentTeamId

        if (isCurrentTeamA && isCurrentTeamB) {
          return 0
        }

        if (isCurrentTeamA || isCurrentTeamB) {
          return isCurrentTeamA ? -1 : 1
        }

        const isRecommendedOrSharedA =
          a.section === ScenarioTypeFilterBy.RecommendedAndShared
        const isRecommendedOrSharedB =
          b.section === ScenarioTypeFilterBy.RecommendedAndShared

        if (isRecommendedOrSharedA && isRecommendedOrSharedB) {
          return 0
        }

        if (isRecommendedOrSharedA || isRecommendedOrSharedB) {
          return isRecommendedOrSharedA ? -1 : 1
        }

        return a.section > b.section ? 1 : -1
      })
  }, [currentTeamId, scenarios, existingScenarios, teamIdMap])

  const filteredScenarioOptions =
    selectedFilterKey === 'all'
      ? scenarioOptions
      : scenarioOptions.filter(
          (scenarioOption) => scenarioOption.filterBy === selectedFilterKey
        )

  // only provide filter menu options that will return > 0 results
  const filterOptionsForCurrentData = useMemo(() => {
    // Collects all the filterBy keys in the current data. 'all' will always be a filter menu option.
    const filterKeySet: Set<FilterOptionKey> = new Set(['all'])

    scenarioOptions.forEach((scenarioOption) => {
      filterKeySet.add(scenarioOption.filterBy)
    })

    // filter out any option that wasn't collected in filterKeySet
    return FILTER_OPTIONS.filter((filterOption) =>
      filterKeySet.has(filterOption.key)
    )
  }, [scenarioOptions])

  const selectedScenario = useMemo(() => {
    if (selectedScenarioOption) {
      return scenariosLookup[selectedScenarioOption.guid]
    }
  }, [scenariosLookup, selectedScenarioOption])

  const shouldShowValidationError =
    selectedScenario && !hasValidScenarioTargets(selectedScenario)

  return (
    <Stack spacing={4}>
      <Stack>
        <Typography variant="h6">Test Suite Composition</Typography>
        <Typography variant="body1">
          Test Suites are made up of scenarios that can be run as tests on a
          service. One scenario maps to one test.
        </Typography>
      </Stack>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
        <FormControl sx={{ width: theme.space[9] }}>
          <InputLabel id="select-filter-scenario-label">
            Filter Scenarios
          </InputLabel>
          <Select<FilterOptionKey>
            label="Filter Scenarios"
            labelId="select-filter-scenario-label"
            value={selectedFilterKey}
            onChange={(e: SelectChangeEvent<FilterOptionKey>) =>
              // TODO: change below if it gets resolved
              // Typecasting is necessary, see https://github.com/mui/material-ui/issues/33399
              setSelectedFilterKey(e.target.value as FilterOptionKey)
            }
          >
            {filterOptionsForCurrentData.map((filterOption) => (
              <MenuItem key={filterOption.key} value={filterOption.key}>
                {filterOption.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Autocomplete
          disableListWrap
          fullWidth
          data-testid="test-suite-scenario-selection-autocomplete"
          getOptionLabel={(option: TestSuiteScenarioOption) =>
            option.label || option.guid
          }
          groupBy={(option) => option.section}
          isOptionEqualToValue={(
            option: TestSuiteScenarioOption,
            value: TestSuiteScenarioOption
          ) => option.guid === value.guid}
          ListboxComponent={VirtualizedListboxComponent}
          ListboxProps={{
            OptionComponent: TestSuiteScenarioSearchOption
          }}
          multiple={false}
          options={filteredScenarioOptions}
          popupIcon={null}
          renderGroup={(params) => params}
          renderInput={(params) => (
            <TextField
              {...params}
              helperText={
                <TestSuiteCompositionInputFieldHelpText
                  {...(shouldShowValidationError && {
                    errorMessage:
                      'This scenario is ineligible because it has both infrastructure and failure flag targets.'
                  })}
                />
              }
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <InputAdornment
                      position="start"
                      sx={{
                        color: 'text.secondary',
                        marginLeft: 1,
                        marginRight: 1
                      }}
                    >
                      <Icon css={{ height: 14 }} icon="magnifyingGlass" />
                    </InputAdornment>
                    {params.InputProps.startAdornment}
                  </>
                )
              }}
              label="Search Scenarios"
            />
          )}
          renderOption={(props, option) => [props, option]}
          value={selectedScenarioOption}
          onChange={handleSelectScenario}
        />
        {selectedScenario && (
          <TestSuiteTestDetailsDrawer
            buttonProps={{
              disabled: shouldShowValidationError,
              startIcon: <Icon icon="plus" />
            }}
            buttonText="Add"
            handleSubmit={handleAddScenario}
            scenario={selectedScenario}
          />
        )}
      </Stack>
    </Stack>
  )
}
